import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import {
  isWwsWeddingWebsiteV2,
  WeddingWebsiteQuery,
  WeddingWebsiteQueryVariables,
} from '../types/graphql';
import { useRouter } from 'next/router';
import Cookies from 'universal-cookie';
import useThemeCoupleOverride from '../lib/useThemeCoupleOverride';
import { makeGuestServicesAPIRequest } from '../lib/guest-fed-wws-result';
import { isSinglePageLayoutFromQuery } from '../lib/isSinglePageLayout';

interface FetchDataArgs {
  variables: WeddingWebsiteQueryVariables;
  context?: {
    sessionToken: string;
  };
}

interface WWSQueryContextProps {
  data: WeddingWebsiteQuery | undefined;
  loading: boolean;
  fetchData: (arg: FetchDataArgs) => Promise<void>;
  // True once every route's page/photo data is available — either because
  // this site isn't route-scoped (single-page layout), or the background
  // fetch of the unfiltered data has completed.
  isFullSiteDataLoaded: boolean;
  // The route SSR fully rendered; that route's data is always complete
  // even before isFullSiteDataLoaded flips to true.
  initialRouteName: string;
}

const WWSQueryContext = createContext<WWSQueryContextProps | undefined>({
  data: undefined,
  loading: false,
  fetchData: (_: FetchDataArgs) => Promise.resolve(), // Default implementation
  isFullSiteDataLoaded: true,
  initialRouteName: '',
});

export const WWSQueryProvider: React.FC<{
  children: ReactNode;
  initialData: WeddingWebsiteQuery | undefined;
}> = ({ children, initialData }) => {
  const { query } = useRouter();
  const [rawData, setRawData] = useState<WeddingWebsiteQuery | undefined>(
    initialData
  );
  const [loading, setLoading] = useState<boolean>(false);
  const loadingRef = useRef(false);

  // Apply override at the top level — valid hook call site
  const data = useThemeCoupleOverride(
    query['useThemeCouple'] === 'true',
    rawData,
    query['themeOverride']
  );

  const fetchData = useCallback(async (params: FetchDataArgs) => {
    if (loadingRef.current) return; // Prevent multiple fetches
    loadingRef.current = true;
    setLoading(true);
    try {
      const fetchedData = await makeGuestServicesAPIRequest(params.variables, {
        ...params.context,
      });

      setRawData(fetchedData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  // SSR only sends full page/photo data for the route requested on first
  // load (see filterRouteScopedData); every other route's images are
  // stripped to null. Templates use isFullSiteDataLoaded/initialRouteName to
  // show a loading state for a route instead of rendering that null data,
  // while this effect fetches the real, unfiltered data once in the
  // background so the wait is usually invisible by the time it's needed.
  const initialRouteNameRef = useRef<string | undefined>(undefined);

  if (typeof initialRouteNameRef.current === 'undefined') {
    const initialRoute = query['route'];
    initialRouteNameRef.current = Array.isArray(initialRoute)
      ? initialRoute[0] || ''
      : initialRoute || '';
  }

  const [isFullSiteDataLoaded, setIsFullSiteDataLoaded] = useState<boolean>(
    () =>
      typeof initialData === 'undefined' ||
      !isWwsWeddingWebsiteV2(initialData.weddingWebsiteResult) ||
      isSinglePageLayoutFromQuery(initialData, query)
  );

  const prefetchTriggeredRef = useRef(false);

  useEffect(() => {
    if (
      prefetchTriggeredRef.current ||
      isFullSiteDataLoaded ||
      typeof data === 'undefined' ||
      !isWwsWeddingWebsiteV2(data.weddingWebsiteResult)
    ) {
      return;
    }

    const slug = query['slug'];

    if (typeof slug !== 'string') {
      return;
    }

    prefetchTriggeredRef.current = true;

    const pin = new Cookies().get('wws-pin') as string | undefined;
    const themeId = query['themeId'];
    // Keep the preview: this result replaces the server-rendered data, which used previewId
    const previewId = query['previewId'];

    fetchData({
      variables: {
        slug,
        pin: pin || '',
        themeId: typeof themeId === 'string' ? themeId : null,
        previewId: typeof previewId === 'string' ? previewId : null,
      },
    }).finally(() => setIsFullSiteDataLoaded(true));
    // Fires once in the background after mount; deliberately not re-run on
    // every query/data change since it only ever needs to happen once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <WWSQueryContext.Provider
      value={{
        data,
        loading,
        fetchData,
        isFullSiteDataLoaded,
        initialRouteName: initialRouteNameRef.current,
      }}
    >
      {children}
    </WWSQueryContext.Provider>
  );
};

export const useWWSQueryContext = (): WWSQueryContextProps => {
  const context = useContext(WWSQueryContext);
  if (!context) {
    throw new Error(
      'useWWSQueryContext must be used within a WWSQueryProvider'
    );
  }
  return context;
};
